import isNull from 'lodash/isNull';
import { FormattedMessage } from 'react-intl';

import { get } from 'utils/lodash';
import { getDisabledAfterDate, getDisabledBeforeDate } from 'utils/transaction';

import { TODAY } from 'settings/constants/calendar';
import { CURRENCY_IDR, DEFAULT_INT_ZERO } from 'settings/constants/common';
import {
  TRANSFER_NOW,
  TRANSFER_FUTURE,
  TRANSFER_RECURRING,
  DEFAULT_MAX_TRANSFER_AMOUNT,
  DEFAULT_MIN_TRANSFER_AMOUNT,
  TRANSFER_RECURRING_WEEKLY,
} from 'settings/constants/transaction';
import { formatArrayKeys } from 'utils/formatter';

import { getIntrabankDetail, getIntrabankFavouriteDetail } from 'middleware/actions/transfer';

export const getIntrabankNote = data => ({
  [TRANSFER_NOW]: get(data, 'mainNote1', ''),
  [TRANSFER_FUTURE]: get(data, 'mainNote2', ''),
  [TRANSFER_RECURRING]: [get(data, 'mainNote3', ''), get(data, 'mainNote4', ''), get(data, 'mainNote5', ''), get(data, 'mainNote6', '')],
});

export const getIntrabankDynamicFields = (detail, senderAccount, isFavouriteTransfer) => {
  const isRecurring = get(detail, 'isRecurring', false);
  const duplicateDateValue = get(detail, 'transferDate', '');
  const transferFrequency = get(detail, 'transferFrequency', '');
  const initialDate = get(detail, 'initialTransferDate', TODAY);
  const currency = get(detail, 'currency', CURRENCY_IDR);
  const currencies = get(detail, 'currencies', []);

  // check if favourite from old m2u without any default value & to avoid conflict with old code
  const favouriteCurrency = get(detail, 'currency', '');

  const transferDateLabel = !isRecurring ? 'transaction.transfer.effectiveDate' : '';
  const disabledAfterDate = getDisabledAfterDate(isRecurring);
  const disabledBeforeDate = (isEndDate = true) =>
    getDisabledBeforeDate({ isRecurring, disabledStartDate: initialDate, isEndDate, duplicateDateValue, initialDate });
  const debitLabel = transferFrequency === TRANSFER_RECURRING_WEEKLY ? 'transaction.transfer.debitDay' : 'transaction.transfer.debitDate';

  const currentBalance = get(senderAccount, 'plainBalance', DEFAULT_INT_ZERO);
  const senderCurrency = get(senderAccount, 'currency', CURRENCY_IDR);

  return [
    {
      name: 'accountNumber',
      readOnly: isFavouriteTransfer,
    },
    {
      name: 'transferAmount',
      hasOption: true,
      optionField: {
        options: currencies,
        isTextField: isFavouriteTransfer && favouriteCurrency,
        name: 'currency',
        readOnly: isFavouriteTransfer && favouriteCurrency,
        validationRules: [{ type: 'required', isValidateRequired: true }],
      },
      validationRules: [
        { type: 'required', isValidateRequired: true },
        { type: 'minAmount', value: DEFAULT_MIN_TRANSFER_AMOUNT, isValidateRequired: true },
        { type: 'maxAmount', value: DEFAULT_MAX_TRANSFER_AMOUNT, isValidateRequired: true },
        {
          type: 'senderBalance',
          value: currentBalance,
          isValidateRequired: senderCurrency === currency,
        },
      ],
    },
    {
      name: 'transferDate',
      label: transferDateLabel,
      disabledAfterDate,
      disabledBeforeDate: disabledBeforeDate(false),
    },
    {
      name: 'transferEndDate',
      duplicateDateValue,
      disabledAfterDate,
      disabledBeforeDate: disabledBeforeDate(),
    },
    {
      name: 'debitDate',
      label: debitLabel,
    },
  ];
};

export const getIntrabankFavouriteDynamicFields = (detail, isFromTransferResult) => {
  const currencies = get(detail, 'currencyList', []);
  const accountLimits = get(detail, 'favouriteLimitList', []);

  return isFromTransferResult
    ? [
        {
          options: formatArrayKeys(accountLimits),
          name: 'accountLimit',
        },
      ]
    : [
        {
          name: 'currencyCode',
          options: formatArrayKeys(currencies),
        },
        {
          name: 'accountLimit',
          options: formatArrayKeys(accountLimits),
        },
      ];
};

export const getIntrabankFavouriteTransferDetail = (detail, isEditFavourite) => {
  const currencyCode = get(detail, 'currencyCode', '');
  const toAccountNo = get(detail, 'toAccountNo', '');
  const emailAddress = get(detail, 'emailAddress', '');
  const accountLimit = get(detail, 'accountLimit', '');
  const accountNickname = get(detail, 'accountNickname', '');
  const toAccountHolderName = get(detail, 'toAccountHolderName', '');

  return { currencyCode, toAccountNo, toAccountHolderName, ...(isEditFavourite && { emailAddress, accountNickname, accountLimit }) };
};

export const getIntrabankFavouriteBeneficiaryDetails = detail => [
  {
    title: <FormattedMessage id="transaction.favourite.form.beneficiaryNickname" />,
    info: get(detail, 'accountNickname', '-'),
  },
  {
    title: <FormattedMessage id="transaction.favourite.form.dailyTransactionLimit" />,
    info: get(detail, 'accountLimitDis', '-'),
  },
  {
    title: <FormattedMessage id="transaction.transfer.beneficiary.email" />,
    info: get(detail, 'emailAddress', '') || get(detail, 'toEmailAddress', '-'),
  },
];

export const getIntrabankEditFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  accountNickname: get(detail, 'confirmationDetail.accountNickname', ''),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
  accountLimit: get(detail, 'confirmationDetail.accountLimit', ''),
  emailAddress: get(detail, 'confirmationDetail.emailAddress', ''),
});

export const getIntrabankDeleteFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
});

export const getIntrabankDetailDispatcher = ({ userId, favouriteIndex, favouriteAccount, dispatch }) => {
  const favAccountMaps = get(favouriteAccount, 'favouriteAccountMap', {});

  return !isNull(favouriteIndex)
    ? dispatch(getIntrabankFavouriteDetail({ userId, favIndex: favouriteIndex, favAccountMaps }))
    : dispatch(getIntrabankDetail({ userId }));
};
